import React, { useEffect, useState } from 'react';
import {
  FEEDBACK_TAGS,
  checkFeedbackEligible,
  submitPlatformFeedback
} from '../utils/platformFeedback';
import { sanitizeUserFacingError } from '../utils/userFacingError';
import './PlatformExperienceSurvey.css';

const PlatformExperienceSurvey = ({
  source = 'footer',
  variant = 'inline',
  initialScore = 0,
  onSuccess,
  onClose,
  showThanks = true
}) => {
  const preset =
    initialScore >= 1 && initialScore <= 5 ? initialScore : 0;
  const [score, setScore] = useState(preset);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [tags, setTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    if (preset >= 1 && preset <= 5) {
      setScore(preset);
    }
  }, [preset]);

  useEffect(() => {
    let cancelled = false;
    checkFeedbackEligible().then((r) => {
      if (cancelled) return;
      if (r && r.canSubmit === false) {
        setBlocked(true);
        setBlockReason(r.reason || 'Thank you for your recent feedback.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleTag = (id) => {
    setTags((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (score < 1) {
      setError('Please select a star rating.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await submitPlatformFeedback({
        score,
        comment,
        tags,
        source,
        page: window.location.pathname
      });
      setDone(true);
      onSuccess?.();
    } catch (err) {
      setError(sanitizeUserFacingError(err.message, 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (blocked) {
    return (
      <div className={`platform-feedback platform-feedback--${variant} platform-feedback--muted`}>
        <p>{blockReason}</p>
      </div>
    );
  }

  if (done && showThanks) {
    return (
      <div
        className={`platform-feedback platform-feedback--${variant} platform-feedback--thanks`}
        role="status"
        aria-live="polite"
      >
        <div className="platform-feedback-thanks-icon" aria-hidden="true">
          ✓
        </div>
        <h3 className="platform-feedback-thanks-title">Thank you!</h3>
        <p className="platform-feedback-thanks-message">
          Your rating has been received. We appreciate you helping us improve Raheasy.
        </p>
        {onClose ? (
          <button type="button" className="platform-feedback-close-btn" onClick={onClose}>
            Close
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <form
      className={`platform-feedback platform-feedback--${variant}`}
      onSubmit={handleSubmit}
    >
      <h3 className="platform-feedback-title">How do you rate your experience with Raheasy?</h3>
      <p className="platform-feedback-sub">Your feedback helps us improve the platform for everyone.</p>

      <div className="platform-feedback-stars" role="radiogroup" aria-label="Rating 1 to 5 stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`platform-feedback-star${(hover || score) >= n ? ' filled' : ''}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setScore(n)}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
      </div>

      <div className="platform-feedback-tags">
        <span className="platform-feedback-tags-label">What was this about? (optional)</span>
        <div className="platform-feedback-tag-list">
          {FEEDBACK_TAGS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`platform-feedback-tag${tags.includes(t.id) ? ' active' : ''}`}
              onClick={() => toggleTag(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <label className="platform-feedback-comment-label">
        Comments (optional)
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 500))}
          maxLength={500}
          rows={variant === 'modal' ? 4 : 3}
          placeholder="Tell us what went well or what we can improve…"
        />
      </label>
      <span className="platform-feedback-char-count">{comment.length}/500</span>

      {error ? <p className="platform-feedback-error">{error}</p> : null}

      <div className="platform-feedback-actions">
        {onClose ? (
          <button type="button" className="platform-feedback-secondary" onClick={onClose}>
            Not now
          </button>
        ) : null}
        <button type="submit" className="platform-feedback-primary" disabled={submitting}>
          {submitting ? 'Sending…' : 'Submit feedback'}
        </button>
      </div>
    </form>
  );
};

export default PlatformExperienceSurvey;
